import React from "react";
import { elakayAutoText } from "./dataElakay";

// The ONE frontend representation of checkout tax. Tax is never computed in
// React: it is read from the server checkout quote (POST /order/quote ->
// elakay_services/payments/orderFinancials.js), the same numbers createOrder
// charges. A lookup that is still running, failed, or was not done is never
// shown as "$0.00" -- only a server-confirmed amount is.
//
// quote: { status: "idle" | "loading" | "ready" | "error", data, code, tax }
// `tax` = the tax part a refused quote still carries (server details.tax):
// tax never depends on shipping, so a line that cannot be shipped keeps its
// tax while the quote (and payment) stays refused.
export const TAX_DISPLAY = Object.freeze({
  AMOUNT: "amount",                 // configured destination rate (0 % included)
  NOT_CHARGED: "not_charged",       // Family tax collection switch is OFF
  NOT_CHARGED_DESTINATION: "not_charged_destination", // country without Elakay tax configuration
  CALCULATING: "calculating",
  AT_CHECKOUT: "at_checkout",       // visitor: no server quote without an account
  ADDRESS_REQUIRED: "address_required",
  UNAVAILABLE: "unavailable",
});

const TAX_ERROR_KINDS = {
  TAX_ADDRESS_INCOMPLETE: TAX_DISPLAY.ADDRESS_REQUIRED,
  TAX_POLICY_UNVERIFIED: TAX_DISPLAY.UNAVAILABLE,
};

export const isTaxErrorCode = (code) => Object.prototype.hasOwnProperty.call(TAX_ERROR_KINDS, code || "");

// Tax data of a quote: the ready quote's, or the tax a refused quote carries.
export const quoteTaxData = (quote) => (quote?.status === "ready" ? quote.data : quote?.status === "error" ? quote.tax : null) || null;

export const taxDisplayState = (quote, { signedIn = true } = {}) => {
  if (!signedIn) return { kind: TAX_DISPLAY.AT_CHECKOUT };
  if (!quote || quote.status === "idle" || quote.status === "loading") return { kind: TAX_DISPLAY.CALCULATING };
  if (quote.status === "error" && !quote.tax) return { kind: TAX_ERROR_KINDS[quote.code] || TAX_DISPLAY.UNAVAILABLE };
  const data = quoteTaxData(quote) || {};
  if (data.taxCollection === "disabled_pending_policy") return { kind: TAX_DISPLAY.NOT_CHARGED, cents: 0 };
  if (data.taxCollection === "not_collected_destination") return { kind: TAX_DISPLAY.NOT_CHARGED_DESTINATION, cents: 0 };
  const cents = Number(data.taxCents);
  return Number.isInteger(cents) ? { kind: TAX_DISPLAY.AMOUNT, cents } : { kind: TAX_DISPLAY.UNAVAILABLE };
};

export const formatUsdCents = (cents) =>
  ((Number(cents) || 0) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Translated reason for a tax error returned by the quote / createOrder.
export const taxErrorReason = (code) => {
  if (code === "TAX_ADDRESS_INCOMPLETE") {
    return elakayAutoText("h_tax_reason_address", "Add a complete delivery address (country, state/province and postal code) to calculate tax.");
  }
  if (code === "TAX_POLICY_UNVERIFIED") {
    return elakayAutoText("h_tax_reason_unverified", "Tax could not be confirmed for this delivery address. Check the postal code or choose another address.");
  }
  return null;
};

// The delivery address has no configured rate: the tax shown comes from the
// shop address of the Seller / Provider (server taxBasis "shop_address").
export const taxFromShopAddress = (quote) => {
  const basis = quoteTaxData(quote)?.taxBasis;
  return basis === "shop_address" || basis === "mixed";
};

export const TaxBasisNote = ({ quote, className = "ck-hint" }) => (taxFromShopAddress(quote) ? (
  <p className={className} data-testid="checkout-tax-basis">
    {elakayAutoText("h_tax_basis_shop", "Tax calculated from the shop's address (no tax rate is configured for this delivery address).")}
  </p>
) : null);

const LABELS = {
  [TAX_DISPLAY.NOT_CHARGED]: () => elakayAutoText("h_tax_not_charged", "Not charged"),
  [TAX_DISPLAY.NOT_CHARGED_DESTINATION]: () => elakayAutoText("h_tax_not_charged_destination", "Not charged for this destination"),
  [TAX_DISPLAY.CALCULATING]: () => elakayAutoText("h_tax_calculating", "Calculating…"),
  [TAX_DISPLAY.AT_CHECKOUT]: () => elakayAutoText("h_tax_at_checkout", "Calculated at checkout"),
  [TAX_DISPLAY.ADDRESS_REQUIRED]: () => elakayAutoText("h_tax_address_required", "Delivery address required"),
  [TAX_DISPLAY.UNAVAILABLE]: () => elakayAutoText("h_tax_unavailable", "Unavailable"),
};

export const TaxQuoteValue = ({ quote, signedIn = true, className = "", testId = "checkout-tax-value" }) => {
  const state = taxDisplayState(quote, { signedIn });
  const text = state.kind === TAX_DISPLAY.AMOUNT ? formatUsdCents(state.cents) : LABELS[state.kind]();
  return (
    <span className={className} data-testid={testId} data-tax-state={state.kind}>
      {text}
    </span>
  );
};

// Tax of an EXISTING order line: always the snapshot stored when it was paid
// (payment.financial, else the legacy taxProduct), never a new lookup with
// today's tax table. null = no tax was recorded for that order.
export const orderLineTaxCents = (line) => {
  const snapshot = line?.payment?.financial;
  if (snapshot && Number.isInteger(snapshot.taxCents)) return snapshot.taxCents;
  const legacy = Number(line?.taxProduct);
  return line?.taxProduct !== undefined && line?.taxProduct !== null && Number.isFinite(legacy) ? Math.round(legacy * 100) : null;
};

export const orderLineTaxText = (line) => {
  const cents = orderLineTaxCents(line);
  return cents === null ? elakayAutoText("h_tax_not_recorded", "Not recorded") : formatUsdCents(cents);
};
